import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";

import { ProfileSchema } from "./schema/profile.schema";

import type { ProfileInfoValues } from "@/types/profile";
import type { ProfileData } from "@/types/account/profile";

import { InputField } from "../../components/common/input-field";
import { useAuthStore } from "@/stores/auth.store";
import { UpdateProfile } from "@/services/account.service";
import { cn } from "@/lib/utils";
import PhoneField from "@/components/common/PhoneField";
import type { DialCode } from "@/types/dialcode";

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 pr-14 text-sm font-medium text-muted-foreground placeholder:text-border";

interface ProfileInfoFormProps {
  profileData: ProfileData | null;
  dialCodes: DialCode[];
  loadingDialCodes: boolean;
  onUpdated: () => Promise<void>;
}

export function ProfileInfoForm({
  profileData,
  dialCodes,
  loadingDialCodes,
  onUpdated,
}: ProfileInfoFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ProfileInfoValues>({
    resolver: zodResolver(ProfileSchema),
    mode: "onChange",
    defaultValues: {
      loginId: "",
      agentName: "",
      email: "",
      contactNoCountryCode: "",
      contactNo: "",
      companyName: "",
    },
  });

  useEffect(() => {
    if (!profileData) return;

    reset({
      loginId: profileData.loginId,
      agentName: profileData.name,
      email: profileData.email,
      contactNoCountryCode: profileData.contactNoCountryCode ?? "",
      contactNo: profileData.contactNo,
      companyName: profileData.companyName,
    });
  }, [profileData, reset]);

  const contactNoCountryCode = watch("contactNoCountryCode");

  const onSubmit = async (data: ProfileInfoValues) => {
    const agentCompanySellerId =
      useAuthStore.getState().user?.agentCompanySellerId;

    if (!agentCompanySellerId) {
      toast.error("Agent company seller ID is missing.");
      return;
    }

    try {
      const name = data.agentName.trim();

      await UpdateProfile({
        agentCompanySellerId,
        name,
        email: data.email.trim(),
        contactNoCountryCode: data.contactNoCountryCode,
        contactNo: data.contactNo.trim(),
        companyName: data.companyName.trim(),
      });

      useAuthStore.getState().updateUserName(name);
      await onUpdated();

      toast.success("Profile updated successfully.");
    } catch (error) {
      console.error("Update profile error:", error);

      toast.error(
        error instanceof Error ? error.message : "Failed to update profile.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-lg font-semibold text-secondary-foreground">
        Profile Information
      </div>

      <form
        className="rounded-2xl bg-card px-4 py-6 shadow-xl"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-4 md:grid md:grid-cols-3">
          <InputField
            label="Login ID"
            placeholder="Enter login ID"
            {...register("loginId")}
            disabled
            className={FIELD_CLASS}
          />

          <InputField
            label="Agent Name"
            placeholder="Enter agent name"
            {...register("agentName")}
            className={cn(
              FIELD_CLASS,
              errors.agentName && "border-destructive",
            )}
            error={errors.agentName?.message}
          />

          <InputField
            label="Email Address"
            placeholder="Enter email address"
            type="email"
            disabled
            {...register("email")}
            className={cn(FIELD_CLASS, errors.email && "border-destructive")}
            error={errors.email?.message}
          />

          <PhoneField
            code={contactNoCountryCode || "Country Code"}
            phone={watch("contactNo") ?? ""}
            dialCodes={dialCodes}
            onCodeChange={(value) => {
              setValue("contactNoCountryCode", value, {
                shouldValidate: true,
                shouldDirty: true,
              });

              trigger(["contactNoCountryCode", "contactNo"]);
            }}
            onPhoneChange={(value) => {
              // Keep maximum input at 15 digits
              const phone = value.replace(/\D/g, "").slice(0, 15);

              // Don't allow 0 as first digit for Malaysia
              if (contactNoCountryCode === "+60" && phone.startsWith("0")) {
                return;
              }

              setValue("contactNo", phone, {
                shouldValidate: true,
                shouldDirty: true,
              });

              if (phone && !contactNoCountryCode) {
                trigger(["contactNoCountryCode", "contactNo"]);
                return;
              }

              // Validate immediately while typing
              trigger("contactNo");
            }}
            error={
              errors.contactNo?.message || errors.contactNoCountryCode?.message
            }
          />

          <InputField
            label="Company Name"
            placeholder="Enter company name"
            {...register("companyName")}
            className={cn(
              FIELD_CLASS,
              errors.companyName && "border-destructive",
            )}
            error={errors.companyName?.message}
          />
        </div>

        <div className="text-end">
          <Button
            variant="default"
            className="mt-4 w-full cursor-pointer md:w-2xs"
            disabled={!isValid || isSubmitting || loadingDialCodes}
            type="submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
